import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Container,
  Group,
  Loader,
  MultiSelect,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core';

import {
  downloadAdminTranscripts,
  getAdminTranscriptDownloads,
} from './api';


const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function dateParts(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
}

function isoDate(year, monthIndex, day) {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function formatDate(value) {
  const parts = dateParts(value);
  if (!parts) {
    return value || '';
  }

  return `${MONTH_LABELS[parts.month - 1]} ${parts.day}, ${parts.year}`;
}

function previousMonth(value) {
  const parts = String(value || '').match(/^(\d{4})-(\d{2})$/);
  if (!parts) {
    return '';
  }

  const next = new Date(Number(parts[1]), Number(parts[2]) - 2, 1, 12, 0, 0);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
}

function nextMonth(value) {
  const parts = String(value || '').match(/^(\d{4})-(\d{2})$/);
  if (!parts) {
    return '';
  }

  const next = new Date(Number(parts[1]), Number(parts[2]), 1, 12, 0, 0);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
}

function sessionMatchesTags(session, selectedTagIds, tagMatch) {
  if (selectedTagIds.length === 0) {
    return true;
  }

  const sessionTagIds = new Set((session?.tag_ids || []).map((value) => Number(value)));
  if (tagMatch === 'and') {
    return selectedTagIds.every((tagId) => sessionTagIds.has(tagId));
  }

  return selectedTagIds.some((tagId) => sessionTagIds.has(tagId));
}

function SessionDatePicker({ availableDates, selectedDates, onChange, filtersActive }) {
  const availableSet = useMemo(() => new Set(availableDates), [availableDates]);
  const selectedSet = useMemo(() => new Set(selectedDates), [selectedDates]);
  const [visibleMonth, setVisibleMonth] = useState('');

  useEffect(() => {
    if (availableDates.length === 0) {
      setVisibleMonth('');
      return;
    }

    // When type/tag filtering changes, start on the most recent month that
    // contains an eligible session. Month navigation after that remains free.
    setVisibleMonth(availableDates[availableDates.length - 1].slice(0, 7));
  }, [availableDates]);

  if (availableDates.length === 0) {
    return (
      <Paper withBorder radius="md" p="lg">
        <Text c="dimmed" size="sm">
          {filtersActive
            ? 'No imported transcript matches the selected session types and personal tags.'
            : 'Select at least one session type that has an imported transcript.'}
        </Text>
      </Paper>
    );
  }

  const monthParts = String(visibleMonth).match(/^(\d{4})-(\d{2})$/);
  if (!monthParts) {
    return null;
  }

  const year = Number(monthParts[1]);
  const monthIndex = Number(monthParts[2]) - 1;
  const firstWeekday = new Date(year, monthIndex, 1, 12, 0, 0).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0, 12, 0, 0).getDate();
  const cells = [];

  for (let i = 0; i < firstWeekday; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(day);
  }

  function toggleDate(value) {
    if (!availableSet.has(value)) {
      return;
    }

    if (selectedSet.has(value)) {
      onChange(selectedDates.filter((item) => item !== value));
    } else {
      onChange([...selectedDates, value].sort());
    }
  }

  return (
    <Paper withBorder radius="md" p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Button
            variant="subtle"
            size="compact-sm"
            onClick={() => setVisibleMonth(previousMonth(visibleMonth))}
            aria-label="Previous month"
          >
            ←
          </Button>
          <Text fw={800}>{MONTH_LABELS[monthIndex]} {year}</Text>
          <Button
            variant="subtle"
            size="compact-sm"
            onClick={() => setVisibleMonth(nextMonth(visibleMonth))}
            aria-label="Next month"
          >
            →
          </Button>
        </Group>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(36px, 1fr))',
            gap: 6,
          }}
        >
          {DAY_LABELS.map((label) => (
            <Text key={label} size="xs" c="dimmed" ta="center" fw={700}>
              {label}
            </Text>
          ))}

          {cells.map((day, index) => {
            if (day === null) {
              return <div key={`blank-${index}`} />;
            }

            const value = isoDate(year, monthIndex, day);
            const available = availableSet.has(value);
            const selected = selectedSet.has(value);

            return (
              <Button
                key={value}
                size="compact-sm"
                variant={selected ? 'filled' : 'subtle'}
                color={selected ? 'blue' : 'gray'}
                disabled={!available}
                onClick={() => toggleDate(value)}
                title={available ? formatDate(value) : 'No session matching the current type/tag filters exists on this date'}
                styles={{
                  root: {
                    minWidth: 0,
                    paddingLeft: 4,
                    paddingRight: 4,
                    opacity: available ? 1 : 0.32,
                  },
                }}
              >
                {day}
              </Button>
            );
          })}
        </div>

        <Group gap="xs" wrap="wrap">
          <Button
            variant="light"
            size="xs"
            onClick={() => onChange([...availableDates])}
          >
            Select all available dates
          </Button>
          <Button
            variant="subtle"
            size="xs"
            disabled={selectedDates.length === 0}
            onClick={() => onChange([])}
          >
            Clear dates
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

export default function AdminTranscriptDownloads() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [tagMatch, setTagMatch] = useState('or');
  const [selectedDates, setSelectedDates] = useState([]);
  const [includeTimestamps, setIncludeTimestamps] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [downloadSuccess, setDownloadSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError('');

      try {
        const result = await getAdminTranscriptDownloads();
        if (cancelled) {
          return;
        }

        setData(result || null);
        setSelectedTypes((result?.session_types || []).map((item) => item.value));
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message || 'Failed to load transcript download options.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const typeFilteredSessions = useMemo(() => {
    const typeSet = new Set(selectedTypes);
    return (data?.sessions || []).filter((session) => typeSet.has(session.session_type));
  }, [data, selectedTypes]);

  const tagCounts = useMemo(() => {
    const counts = new Map();
    typeFilteredSessions.forEach((session) => {
      const uniqueIds = new Set((session?.tag_ids || []).map((value) => Number(value)));
      uniqueIds.forEach((tagId) => counts.set(tagId, (counts.get(tagId) || 0) + 1));
    });
    return counts;
  }, [typeFilteredSessions]);

  const tagSelectData = useMemo(() => (
    (data?.tags || []).map((tag) => ({
      value: String(tag.id),
      label: `${tag.name} (${tagCounts.get(Number(tag.id)) || 0})`,
    }))
  ), [data, tagCounts]);

  const eligibleSessions = useMemo(() => (
    typeFilteredSessions.filter((session) => sessionMatchesTags(session, selectedTagIds, tagMatch))
  ), [typeFilteredSessions, selectedTagIds, tagMatch]);

  const availableDates = useMemo(() => Array.from(new Set(
    eligibleSessions.map((session) => session.session_date)
  )).sort(), [eligibleSessions]);

  useEffect(() => {
    const availableSet = new Set(availableDates);
    setSelectedDates((current) => current.filter((value) => availableSet.has(value)));
  }, [availableDates]);

  const selectedSessions = useMemo(() => {
    const dateSet = new Set(selectedDates);
    return eligibleSessions.filter((session) => dateSet.has(session.session_date));
  }, [eligibleSessions, selectedDates]);

  function clearMessages() {
    setDownloadError('');
    setDownloadSuccess('');
  }

  function toggleSessionType(value) {
    clearMessages();
    setSelectedTypes((current) => (
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    ));
  }

  function changeTags(values) {
    clearMessages();
    setSelectedTagIds(
      values
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value > 0)
    );
  }

  async function handleDownload() {
    if (selectedTypes.length === 0) {
      setDownloadError('Select at least one session type.');
      return;
    }
    if (selectedDates.length === 0) {
      setDownloadError('Select at least one session date.');
      return;
    }

    setDownloading(true);
    setDownloadError('');
    setDownloadSuccess('');

    try {
      const result = await downloadAdminTranscripts({
        sessionTypes: selectedTypes,
        sessionDates: selectedDates,
        tagIds: selectedTagIds,
        tagMatch,
        includeTimestamps,
      });

      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = result.filename || 'davealyzer_transcripts.txt';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);

      setDownloadSuccess(
        `Downloaded ${selectedSessions.length} transcript${selectedSessions.length === 1 ? '' : 's'} `
        + `as ${anchor.download}.`
      );
    } catch (err) {
      setDownloadError(err.message || 'The transcript download failed.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="center" wrap="wrap">
          <Button component="a" href="/" variant="subtle" px={0}>
            ← Back to Davealyzer
          </Button>
          <Badge color="green" variant="light">Administrator</Badge>
        </Group>

        <Stack gap={3}>
          <Title order={2}>Download Transcripts</Title>
          <Text size="sm" c="dimmed">
            Export complete imported transcripts for selected Davealyzer sessions as one UTF-8 text file.
          </Text>
          <Text size="sm" c="dimmed">
            Filter by session type, your personal tags, and date. Q&A exports preserve Instructor and Student speaker turns.
          </Text>
        </Stack>

        {loadError && <Alert color="red" title="Unable to load transcripts">{loadError}</Alert>}
        {downloadError && <Alert color="red" title="Download failed">{downloadError}</Alert>}
        {downloadSuccess && <Alert color="green" title="Download complete">{downloadSuccess}</Alert>}

        {loading ? (
          <Group justify="center" py="xl"><Loader /></Group>
        ) : (
          <>
            <Paper withBorder radius="md" p="lg">
              <Stack gap="md">
                <Stack gap={2}>
                  <Text fw={800}>1. Session types</Text>
                  <Text size="sm" c="dimmed">
                    Check every type you want included. Tag counts and available dates immediately follow this selection.
                  </Text>
                </Stack>

                <Group gap="lg" wrap="wrap">
                  {(data?.session_types || []).map((item) => (
                    <Checkbox
                      key={item.value}
                      checked={selectedTypes.includes(item.value)}
                      onChange={() => toggleSessionType(item.value)}
                      label={`${item.label} (${item.session_count})`}
                    />
                  ))}
                </Group>
              </Stack>
            </Paper>

            <Paper withBorder radius="md" p="lg">
              <Stack gap="md">
                <Group justify="space-between" align="end" wrap="wrap">
                  <Stack gap={2}>
                    <Text fw={800}>2. Personal tags</Text>
                    <Text size="sm" c="dimmed">
                      Optional. A session qualifies when the selected tag appears anywhere in it: on the session itself, a Q&A answer, or a search-result clip.
                    </Text>
                  </Stack>
                  <Badge color="violet" variant="light">
                    {eligibleSessions.length} qualifying session{eligibleSessions.length === 1 ? '' : 's'}
                  </Badge>
                </Group>

                {(data?.tags || []).length > 0 ? (
                  <>
                    <MultiSelect
                      label="Filter by my tags"
                      placeholder="No tag filter — include all matching sessions"
                      data={tagSelectData}
                      value={selectedTagIds.map((value) => String(value))}
                      onChange={changeTags}
                      searchable
                      clearable
                      hidePickedOptions
                    />

                    {selectedTagIds.length > 1 && (
                      <Group gap="sm" align="center">
                        <Text size="sm" fw={700}>Match selected tags:</Text>
                        <SegmentedControl
                          size="xs"
                          value={tagMatch}
                          onChange={(value) => {
                            clearMessages();
                            setTagMatch(value);
                          }}
                          data={[
                            { label: 'OR', value: 'or' },
                            { label: 'AND', value: 'and' },
                          ]}
                        />
                        <Text size="xs" c="dimmed">
                          {tagMatch === 'and'
                            ? 'Session must contain every selected tag.'
                            : 'Session may contain any selected tag.'}
                        </Text>
                      </Group>
                    )}
                  </>
                ) : (
                  <Text size="sm" c="dimmed">
                    You have not created any personal tags yet. Transcript download still works normally by type and date.
                  </Text>
                )}
              </Stack>
            </Paper>

            <Stack gap="sm">
              <Group justify="space-between" align="end" wrap="wrap">
                <Stack gap={2}>
                  <Text fw={800}>3. Session dates</Text>
                  <Text size="sm" c="dimmed">
                    Choose one or more dates. Only dates containing a transcript that matches the current type/tag filters are enabled.
                  </Text>
                </Stack>
                <Badge variant="light">
                  {availableDates.length} available date{availableDates.length === 1 ? '' : 's'}
                </Badge>
              </Group>

              <SessionDatePicker
                availableDates={availableDates}
                selectedDates={selectedDates}
                onChange={setSelectedDates}
                filtersActive={selectedTypes.length > 0 || selectedTagIds.length > 0}
              />

              {selectedDates.length > 0 && (
                <Group gap="xs" wrap="wrap">
                  {selectedDates.map((value) => (
                    <Badge key={value} variant="outline" size="lg">
                      {formatDate(value)}
                    </Badge>
                  ))}
                </Group>
              )}
            </Stack>

            <Paper withBorder radius="md" p="lg">
              <Stack gap="md">
                <Stack gap={2}>
                  <Text fw={800}>4. Transcript format</Text>
                  <Text size="sm" c="dimmed">
                    Every transcript starts with its session type, date, and title.
                  </Text>
                </Stack>

                <Checkbox
                  checked={includeTimestamps}
                  onChange={(event) => setIncludeTimestamps(event.currentTarget.checked)}
                  label="Include timestamps"
                  description="When selected, transcript segments include HH:MM:SS timestamps from the beginning of the session."
                />
              </Stack>
            </Paper>

            <Paper withBorder radius="md" p="lg">
              <Group justify="space-between" align="center" wrap="wrap">
                <Stack gap={2}>
                  <Text fw={800}>Ready to export</Text>
                  <Text size="sm" c="dimmed">
                    {selectedSessions.length} session{selectedSessions.length === 1 ? '' : 's'} will be included in one TXT file.
                  </Text>
                  {selectedTagIds.length > 0 && (
                    <Text size="xs" c="dimmed">
                      Personal tag filter: {selectedTagIds.length} selected ({tagMatch.toUpperCase()} match).
                    </Text>
                  )}
                </Stack>

                <Button
                  color="green"
                  size="md"
                  loading={downloading}
                  disabled={selectedSessions.length === 0 || selectedTypes.length === 0 || selectedDates.length === 0}
                  onClick={handleDownload}
                >
                  Download Transcripts
                </Button>
              </Group>
            </Paper>
          </>
        )}
      </Stack>
    </Container>
  );
}
